import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { ReportContext } from '../contexts/ReportContext';
import { getEmptyUserDataExportLines } from '../utils/getEmptyUserDataExportLines';
import { isTimeTrackerReportEmpty } from '../utils/isTimeTrackerReportEmpty';

/**
 * Builds EmptyUserData export lines and empty-state flag for report downloads.
 * @param {Object} params
 * @param {string} [params.reportType='']
 * @param {Object|null} [params.reportData=null] - Parsed report wrapper with `data` property
 * @param {Object|null} [params.modificationEmptyState=null] - { user, message } for modifications
 * @param {string} [params.queriedPeriod=''] - Pre-formatted period from useReportManagerDt
 * @returns {{ emptyUserDataLines: string[], isReportEmpty: boolean }}
 */
export const useEmptyUserDataExportParams = ({
  reportType = '',
  reportData = null,
  modificationEmptyState = null,
  queriedPeriod = '',
} = {}) => {
  const intl = useIntl();
  const location = useLocation();
  const { selectedIndividualUser } = useContext(ReportContext);

  const { startDate, endDate } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      startDate: query.get('startDate'),
      endDate: query.get('endDate'),
    };
  }, [location.search]);

  const emptyUserDataLines = useMemo(
    () =>
      getEmptyUserDataExportLines(intl, {
        reportType,
        selectedIndividualUser,
        startDate,
        endDate,
        queriedPeriod,
        modificationEmptyState,
      }),
    [intl, reportType, selectedIndividualUser, startDate, endDate, queriedPeriod, modificationEmptyState],
  );

  const isReportEmpty = useMemo(
    () => isTimeTrackerReportEmpty(reportData),
    [reportData],
  );

  return { emptyUserDataLines, isReportEmpty };
};
